/**
 * serverFailure.ts — sporočilo, ko zahteva ni prišla do odločitve strežnika (T2477)
 * ================================================================================
 *
 * Javni obrazci (prijava, registracija, pozabljeno geslo, nastavitev gesla) so
 * vsako napako brez znane kode prevedli v svoje privzeto besedilo. Na prijavi je
 * bilo to »Napačen email ali geslo.« — tudi ko strežnik **sploh ni odgovoril**
 * (`ERR_CONNECTION_REFUSED`, axios brez `response`) ali je padel s 5xx. Uporabnik
 * je zato začel ponastavljati geslo, ki je bilo pravilno.
 *
 * Funkcija loči oba primera, ki o vnosu uporabnika ne povesta ničesar:
 *
 * - **ni odgovora** (omrežna napaka, prekinjena povezava, časovna omejitev) →
 *   `common.error_server_unreachable`;
 * - **5xx brez kode in besedila** (padec strežnika, stran posrednika) →
 *   splošna napaka (privzeto `common.error_generic`, klicatelj lahko poda
 *   svojo, npr. `register.error_generic`). 5xx s kodo ali besedilom (503
 *   `MAINTENANCE_MODE`) ostane klicatelju, ki izpiše besedilo strežnika.
 *
 * Za vse ostalo vrne `null` in klicatelj napako obravnava po svoje — tako kot
 * `throttleMessage()`.
 *
 * ⚠️ »Ni odgovora« velja samo za napako **axiosa** (`isAxiosError` ali
 * prisoten `request`), in ne za preklic (`ERR_CANCELED`) ali napako
 * konfiguracije odjemalca. Navadna izjema iz kode odjemalca prav tako nima
 * `response`, a strežnik zanjo ni kriv — zanjo funkcija vrne `null`.
 */
import { asApiError } from './apiError';

/** Ključ sporočila, ko strežnik ni odgovoril. */
export const SERVER_UNREACHABLE_KEY = 'common.error_server_unreachable';

interface AxiosLike {
  isAxiosError?: unknown;
  request?: unknown;
  code?: unknown;
  __CANCEL__?: unknown;
}

/**
 * Kode axiosa, ki nimajo odgovora, a strežnik zanje ni kriv: preklic zahteve
 * (zapustitev strani, `AbortController`) in napake konfiguracije odjemalca.
 */
const NI_OMREZNA_NAPAKA = new Set([
  'ERR_CANCELED',
  'ERR_BAD_OPTION',
  'ERR_BAD_OPTION_VALUE',
  'ERR_INVALID_URL',
  'ERR_NOT_SUPPORT',
  'ERR_DEPRECATED',
]);

/** Ali je napaka axiosova napaka brez odgovora strežnika (omrežje, timeout)? */
export function isNoResponseError(err: unknown): boolean {
  if (typeof err !== 'object' || err === null) return false;
  if (asApiError(err).response) return false;
  const e = err as AxiosLike;
  // `axios.isCancel()` preverja prav `__CANCEL__`; uvoz axiosa tu ni potreben.
  if (e.__CANCEL__ === true) return false;
  if (typeof e.code === 'string' && NI_OMREZNA_NAPAKA.has(e.code)) return false;
  return e.isAxiosError === true || e.request !== undefined;
}

/**
 * Ali strežnik o zahtevi ni odločil — ni odgovoril ali je vrnil 5xx (T2486)?
 *
 * Za zaslone, ki iz napake sklepajo o **stanju** (npr. »povezava je potekla«)
 * in ne le izpišejo sporočila: tak sklep ob padcu strežnika ni upravičen.
 */
export function isServerFailure(err: unknown): boolean {
  if (isNoResponseError(err)) return true;
  const status = asApiError(err).response?.status;
  return typeof status === 'number' && status >= 500;
}

/**
 * Sporočilo za napako, ki ni odločitev strežnika o vnosu, ali `null`.
 *
 * @param genericMessage besedilo za 5xx; privzeto `common.error_generic`.
 */
export function serverFailureMessage(
  err: unknown,
  t: (key: string) => string,
  genericMessage?: string,
): string | null {
  if (isNoResponseError(err)) {
    return t(SERVER_UNREACHABLE_KEY);
  }
  const response = asApiError(err).response;
  const status = response?.status;
  if (typeof status === 'number' && status >= 500) {
    // 5xx, ki nosi svojo kodo ali besedilo (npr. 503 `MAINTENANCE_MODE` iz
    // `MaintenanceModeMiddleware`), pove vzrok bolje od splošnega stavka —
    // prepusti ga obstoječi veji klicatelja.
    const data = response?.data;
    // Samo `error` kot niz: klicatelji berejo `data.error`; 5xx s samo `code`
    // bi sicer padel v njihovo privzeto vejo (npr. »Napačen email ali geslo«).
    if (typeof data?.error === 'string') return null;
    return genericMessage ?? t('common.error_generic');
  }
  return null;
}
